import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

// components
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import PersonalizedTheme from './components/personalizedTheme';
import './App.css';
import { AlertProvider } from './context/AlertContext';
import { AnimatePresence } from 'framer-motion';
import { Alert } from './pages/Alert';
import { lazyRetry } from './utils/general';
import { Toast } from './components/Toast';
import { AuthInterceptorComponent } from '@/components/AuthInterceptorComponent';
import { BottomNavbar } from '@/components/BottomNavbar';
import { Sidebar, SidebarTopBar } from '@/components/Sidebar';
import { Toaster } from 'react-hot-toast';
import Routes from '@/routes';
import ErrorBoundary from '@/common/errorBoundary';
import { HackuChatWidget } from '@/components/HackuChatWidget';
import { BackgroudGradient } from '@/common/components/BackgroudGradient';
import { LoadingProvider, useLoading } from '@/context/LoadingContext';
import { SidebarProvider, useSidebarOpen } from '@/context/SidebarContext';
import { useBreakpoint } from '@/hooks';
import { useLocation } from 'react-router-dom';

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    shade: Palette['primary'];
    gold: Palette['primary'];
    fire: Palette['primary'];
    loaded: boolean;
  }
  interface PaletteOptions {
    shade?: PaletteOptions['primary'];
    gold?: PaletteOptions['primary'];
    fire?: Palette['primary'];
    loaded?: boolean;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    fontSizes: {
      title: string;
      subtitle: string;
      body: string;
      caption: string;
    };
  }
  interface ThemeOptions {
    fontSizes?: {
      title?: string;
      subtitle?: string;
      body?: string;
      caption?: string;
    };
  }
}

const NotificationSurvey = React.lazy(() =>
  lazyRetry(() => import('./pages/Notification/NotificationSurvey')),
);

// Component to handle Suspense loading state
function SuspenseHandler({ children }: { children: React.ReactNode }) {
  const { setLoading } = useLoading();

  useEffect(() => {
    // When this component mounts, we're not in suspense
    setLoading(false, 'Suspense');
    // Also clear the initial loading state
    setLoading(false, 'initial');

    return () => {
      // Cleanup
      setLoading(false, 'Suspense');
    };
  }, [setLoading]);

  return <>{children}</>;
}

const CONTENT_SCREEN_PATHS = ['/content/detail', '/content/evaluation'];

function AppLayout() {
  const { open } = useSidebarOpen();
  const { isDesktop } = useBreakpoint();
  const { pathname } = useLocation();
  const isContentScreen = CONTENT_SCREEN_PATHS.some((p) =>
    pathname.startsWith(p),
  );
  return (
    <>
      {/* Desktop sidebar */}
      <Sidebar />

      <div
        className="container"
        data-sidebar-open={open}
        style={{
          // Space for fixed BottomNavbar (~70px) + safe area on mobile
          paddingBottom: 'calc(70px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {isDesktop ? <SidebarTopBar /> : <Navbar />}
        <Toast />
        <Toaster /> {/* React host toast library */}
        <BottomNavbar />
        <div className="content">
          <Alert />
          <NotificationSurvey />
          <AnimatePresence mode="wait">
            <Routes />
          </AnimatePresence>
        </div>
        <div className="footer">
          <Footer />
        </div>
      </div>
      {!isContentScreen && <HackuChatWidget />}
    </>
  );
}

function AppContent() {
  return (
    <Router>
      <ErrorBoundary>
        <AuthInterceptorComponent>
          <BackgroudGradient />
          <PersonalizedTheme>
            <SidebarProvider>
              <Suspense fallback={<SuspenseHandler>{null}</SuspenseHandler>}>
                <SuspenseHandler>
                  <AppLayout />
                </SuspenseHandler>
              </Suspense>
            </SidebarProvider>
          </PersonalizedTheme>
        </AuthInterceptorComponent>
      </ErrorBoundary>
    </Router>
  );
}

function App() {
  return (
    <LoadingProvider>
      <AppContent />
    </LoadingProvider>
  );
}

function AlertContex(props: any) {
  return (
    <AlertProvider>
      <App {...props} />
    </AlertProvider>
  );
}

export default AlertContex;
